import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SearchStats, StatisticsService } from '../../services/statistics.service';
import { AdminNav } from '../../components/admin-nav/admin-nav';

// SVG chart constants
const SVG_W = 800;
const SVG_H = 200;
const PAD = { top: 12, right: 16, bottom: 36, left: 44 };
const CHART_W = SVG_W - PAD.left - PAD.right;  // 740
const CHART_H = SVG_H - PAD.top - PAD.bottom;   // 152

@Component({
  selector: 'app-statistics',
  imports: [CommonModule, AdminNav],
  templateUrl: './statistics.html',
  styleUrl: './statistics.scss',
})
export class Statistics implements OnInit {

  stats: SearchStats | null = null;
  loading = true;
  error = false;
  selectedDays = 1;

  readonly SOURCE_LABELS: Record<string, string> = {
    DB_GEOHASH: 'BD Geohash (cat+loc)',
    DB_GEOHASH_NEAR_ME: 'BD Geohash (cerca)',
    DB_CATEGORY: 'BD Categoría',
    DB_SEARCH_CACHE: 'BD Caché',
    GOOGLE_PLACES: 'Google Places',
    NONE: 'Sin resultados',
  };

  readonly TYPE_LABELS: Record<string, string> = {
    FILTERED_SEARCH: 'Filtrada (cat/loc)',
    NAME_SEARCH: 'Por nombre',
    FULL_SEARCH: 'Búsqueda libre',
  };

  constructor(
    private statsService: StatisticsService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.loadStats();
  }

  async loadStats() {
    this.loading = true;
    this.error = false;
    try {
      this.stats = await this.statsService.getSearchStats(this.selectedDays);
    } catch (e) {
      console.error('Error loading stats:', e);
      this.error = true;
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  onDaysChange(days: number) {
    this.selectedDays = days;
    this.loadStats();
  }

  getBarWidth(value: number, max: number): string {
    if (max === 0) return '0%';
    return Math.round((value / max) * 100) + '%';
  }

  getMaxValue(obj: Record<string, number>): number {
    return Math.max(...Object.values(obj), 1);
  }

  getTrendMax(): number {
    if (!this.stats) return 1;
    return Math.max(...this.stats.dailyTrend.map(d => d.total), 1);
  }

  formatDate(dateStr: string): string {
    const [, month, day] = dateStr.split('-');
    return `${day}/${month}`;
  }

  objectEntries(obj: Record<string, number>): [string, number][] {
    return Object.entries(obj).sort((a, b) => b[1] - a[1]);
  }

  sourceLabel(key: string): string {
    return this.SOURCE_LABELS[key] ?? key;
  }

  typeLabel(key: string): string {
    return this.TYPE_LABELS[key] ?? key;
  }

  // ─── SVG Chart helpers ───────────────────────────────────

  get svgViewBox(): string {
    return `0 0 ${SVG_W} ${SVG_H}`;
  }

  private trendX(i: number, n: number): number {
    if (n <= 1) return PAD.left + CHART_W / 2;
    return PAD.left + (i / (n - 1)) * CHART_W;
  }

  private trendY(value: number, max: number): number {
    return PAD.top + CHART_H - (value / max) * CHART_H;
  }

  trendLinePath(metric: 'db' | 'google'): string {
    const data = this.stats?.dailyTrend;
    if (!data || data.length === 0) return '';
    const max = this.getTrendMax();
    const n = data.length;
    return data
      .map((d, i) => {
        const v = metric === 'db' ? d.dbCalls : d.googleCalls;
        return `${i === 0 ? 'M' : 'L'}${this.trendX(i, n).toFixed(1)},${this.trendY(v, max).toFixed(1)}`;
      })
      .join(' ');
  }

  trendAreaPath(metric: 'db' | 'google'): string {
    const data = this.stats?.dailyTrend;
    if (!data || data.length === 0) return '';
    const max = this.getTrendMax();
    const n = data.length;
    const bottom = (PAD.top + CHART_H).toFixed(1);
    const line = data
      .map((d, i) => {
        const v = metric === 'db' ? d.dbCalls : d.googleCalls;
        return `${this.trendX(i, n).toFixed(1)},${this.trendY(v, max).toFixed(1)}`;
      })
      .join(' L');
    const x0 = this.trendX(0, n).toFixed(1);
    const xN = this.trendX(n - 1, n).toFixed(1);
    return `M${x0},${bottom} L${line} L${xN},${bottom} Z`;
  }

  trendGridLines(): { y: number; label: string }[] {
    const max = this.getTrendMax();
    const steps = 4;
    return Array.from({ length: steps + 1 }, (_, i) => {
      const frac = i / steps;
      const value = Math.round(max * frac);
      return { y: PAD.top + CHART_H - frac * CHART_H, label: String(value) };
    });
  }

  trendXLabels(): { x: number; label: string }[] {
    const data = this.stats?.dailyTrend;
    if (!data || data.length === 0) return [];
    const n = data.length;
    // Show at most ~7 labels regardless of range
    const step = Math.max(1, Math.ceil(n / 7));
    return data
      .map((d, i) => ({ index: i, label: this.formatDate(d.date) }))
      .filter(({ index }) => index % step === 0 || index === n - 1)
      .map(({ index, label }) => ({ x: this.trendX(index, n), label }));
  }

  trendDots(metric: 'db' | 'google'): { cx: number; cy: number; value: number }[] {
    const data = this.stats?.dailyTrend;
    // Only show dots when there are few points (≤7)
    if (!data || data.length > 7) return [];
    const max = this.getTrendMax();
    const n = data.length;
    return data.map((d, i) => ({
      cx: this.trendX(i, n),
      cy: this.trendY(metric === 'db' ? d.dbCalls : d.googleCalls, max),
      value: metric === 'db' ? d.dbCalls : d.googleCalls,
    }));
  }
}
